import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Box, Button } from '@mui/material'
import {
  PbiReportDefinitionViewModel,
  PbiChildReportViewModel,
} from '@rsmus/ecp-userservice'
import { useTranslation } from 'react-i18next'
import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'
import {
  PBI_CHILD_REPORT_COLUMNS,
  mapPbiChildReportSortKey,
} from '../../utils/constants/constants'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert'
import api from '../../api'
import ChildReportModal from './ChildReportModal'
import ConfirmRemoveChildReportDialog from './ConfirmRemoveChildReportDialog'
import AdminPbiChildReportResults from './AdminPbiChildReportResults'
import {
  useUpdateReportSearchResultsMutation,
  useUpdateChildReportSearchResultsMutation,
  resetChildReportSearch,
} from '../../store/adminSettings/PbiReportSearchService'

type ExtendedReportDefinition = PbiReportDefinitionViewModel

type PbiChildReportRow = PbiChildReportViewModel & {
  parentReportName?: string | null
}

const DEFAULT_PBI_CHILD_REPORT_PAGE_NUMBER = 1
const DEFAULT_PBI_CHILD_REPORT_PAGE_SIZE = 10 as RsmPaginationPageSizes
const DEFAULT_PBI_CHILD_REPORT_SORT_COLUMN =
  PBI_CHILD_REPORT_COLUMNS.DISPLAY_NAME
const DEFAULT_PBI_CHILD_REPORT_SORT_DIRECTION: 'asc' | 'desc' = 'asc'

const AdminSettingsPbiChildReport = () => {
  const { t } = useTranslation()

  const [updateChildReportSearchResults] =
    useUpdateChildReportSearchResultsMutation()
  const [updateReportSearchResults] = useUpdateReportSearchResultsMutation()

  const [openAddChildReport, setOpenAddChildReport] = useState(false)
  const [openEditChildReport, setOpenEditChildReport] = useState(false)
  const [openRemoveConfirm, setOpenRemoveConfirm] = useState(false)

  const [selectedChildReport, setSelectedChildReport] =
    useState<PbiChildReportViewModel | null>(null)

  const [isSaveChildReportSuccess, setIsSaveChildReportSuccess] =
    useState(false)
  const [isEditSaveChildReportSuccess, setIsEditSaveChildReportSuccess] =
    useState(false)
  const [isRemoveSaveChildReportSuccess, setIsRemoveSaveChildReportSuccess] =
    useState(false)

  const [childReportsData, setChildReportsData] = useState<{
    data: PbiChildReportRow[]
    totalRows: number
  }>({ data: [], totalRows: 0 })

  const [reportDefinitions, setReportDefinitions] = useState<
    ExtendedReportDefinition[]
  >([])

  const [isChildReportError, setIsChildReportError] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  const [pageNumber, setPageNumber] = useState(
    DEFAULT_PBI_CHILD_REPORT_PAGE_NUMBER,
  )
  const [pageSize, setPageSize] = useState(DEFAULT_PBI_CHILD_REPORT_PAGE_SIZE)
  const [sortColumn, setSortColumn] = useState<string>(
    DEFAULT_PBI_CHILD_REPORT_SORT_COLUMN,
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    DEFAULT_PBI_CHILD_REPORT_SORT_DIRECTION,
  )

  const fetchChildReports = useCallback(async () => {
    const filter = {
      pageNumber,
      pageSize,
      sortBy: sortColumn,
      isSortAscending: sortDirection === 'asc',
    }

    try {
      const result = await updateChildReportSearchResults(filter).unwrap()

      setChildReportsData({
        data: result.data as PbiChildReportViewModel[],
        totalRows: result.totalRows,
      })
      setIsChildReportError(false)
    } catch {
      setIsChildReportError(true)
      setChildReportsData({ data: [], totalRows: 0 })
    } finally {
      setIsMounted(true)
    }
  }, [
    pageNumber,
    pageSize,
    sortColumn,
    sortDirection,
    updateChildReportSearchResults,
  ])

  const fetchReportDefinitions = useCallback(async () => {
    try {
      const result = await updateReportSearchResults({
        pageNumber: 1,
        pageSize: 1000,
        sortBy: 'ReportName',
        isSortAscending: true,
      }).unwrap()

      setReportDefinitions(result.data as ExtendedReportDefinition[])
    } catch {
      // silent fail for dropdown population
    }
  }, [updateReportSearchResults])

  useEffect(() => {
    fetchChildReports()
  }, [fetchChildReports])

  useEffect(() => {
    fetchReportDefinitions()
    return () => {
      resetChildReportSearch()
    }
  }, [fetchReportDefinitions])

  const handleSorting = useCallback(
    (columnName: string, direction: 'asc' | 'desc') => {
      const mappedColumn = mapPbiChildReportSortKey(columnName)
      setSortColumn(mappedColumn)
      setSortDirection(direction)
      setPageNumber(DEFAULT_PBI_CHILD_REPORT_PAGE_NUMBER)
    },
    [],
  )

  const handlePageChange = useCallback(
    (newPageNumber: number, newPageSize: RsmPaginationPageSizes) => {
      setPageNumber(newPageNumber)
      setPageSize(newPageSize)
    },
    [],
  )

  const handleOpenAddModal = useCallback(() => {
    setSelectedChildReport(null)
    setOpenAddChildReport(true)
  }, [])

  const handleEditChildReport = useCallback(
    (childReport: PbiChildReportViewModel) => {
      setSelectedChildReport(childReport)
      setOpenEditChildReport(true)
    },
    [],
  )

  const handleAddNewChildReport = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_childReport: PbiChildReportViewModel) => {
      setOpenAddChildReport(false)
      setIsSaveChildReportSuccess(true)
      fetchChildReports()
    },
    [fetchChildReports],
  )

  const handleUpdateChildReport = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_childReport: PbiChildReportViewModel) => {
      setOpenEditChildReport(false)
      setIsEditSaveChildReportSuccess(true)
      fetchChildReports()
    },
    [fetchChildReports],
  )

  const handleAskRemoveChildReport = useCallback(
    (childReport: PbiChildReportViewModel) => {
      setSelectedChildReport(childReport)
      setOpenRemoveConfirm(true)
    },
    [],
  )

  const confirmRemoveChildReport = useCallback(async () => {
    if (!selectedChildReport?.id) return

    try {
      await api.user.report_RemoveChildReport(selectedChildReport.id, undefined)

      setChildReportsData((prev) => ({
        data: prev.data.filter((item) => item.id !== selectedChildReport.id),
        totalRows: Math.max(0, prev.totalRows - 1),
      }))

      setIsRemoveSaveChildReportSuccess(true)
    } catch {
      setIsChildReportError(true)
    } finally {
      setOpenRemoveConfirm(false)
      setSelectedChildReport(null)
    }
  }, [selectedChildReport])

  const handleCloseAddModal = useCallback(() => {
    setOpenAddChildReport(false)
    setSelectedChildReport(null)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setOpenEditChildReport(false)
    setSelectedChildReport(null)
  }, [])

  const handleCloseRemoveDialog = useCallback(() => {
    setOpenRemoveConfirm(false)
    setSelectedChildReport(null)
  }, [])

  const getParentReportName = (): string => {
    if (!(selectedChildReport as any)?.pbiReportDefinitionId) return ''

    const definition = reportDefinitions.find(
      (d) => d.id === (selectedChildReport as any)?.pbiReportDefinitionId,
    )

    return definition?.reportName ?? ''
  }

  const childReportsDataWithNames = useMemo(() => {
    const definitionNameById = new Map<number, string>(
      reportDefinitions.map((d) => [d.id, d.reportName ?? '']),
    )

    return {
      totalRows: childReportsData.totalRows,
      data: childReportsData.data.map((childReport: any) => ({
        ...childReport,
        parentReportName:
          definitionNameById.get(childReport.pbiReportDefinitionId ?? 0) ?? '',
      })),
    }
  }, [childReportsData, reportDefinitions])

  const eligibleReportDefinitions = useMemo(
    () =>
      reportDefinitions.filter(
        (report) => (report as any).effectiveIdentityEnabled === true,
      ),
    [reportDefinitions],
  )

  return (
    <Box>
      <Box sx={{ marginBottom: '1rem' }}>
        <Button
          variant="contained"
          onClick={handleOpenAddModal}
          data-testid="Btn_AdminSettingsForm_AddNewPbiChildReport">
          {t('PbiChildReport.AddNewChildReport', 'Add Child Report')}
        </Button>
      </Box>

      <AdminPbiChildReportResults
        childReportsData={childReportsDataWithNames}
        isChildReportError={isChildReportError}
        isMounted={isMounted}
        pageNumber={pageNumber}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSorting={handleSorting}
        onEditChildReport={handleEditChildReport}
        onRemoveChildReport={handleAskRemoveChildReport}
      />

      <ChildReportModal
        open={openAddChildReport}
        onClose={handleCloseAddModal}
        onSave={handleAddNewChildReport}
        mode="create"
        reportDefinitions={eligibleReportDefinitions}
      />

      <ChildReportModal
        open={openEditChildReport}
        onClose={handleCloseEditModal}
        onUpdate={handleUpdateChildReport}
        onRemove={() => {
          setOpenEditChildReport(false)
          if (selectedChildReport) {
            handleAskRemoveChildReport(selectedChildReport)
          }
        }}
        mode="edit"
        initialData={selectedChildReport ?? undefined}
        reportDefinitions={eligibleReportDefinitions}
      />

      <ConfirmRemoveChildReportDialog
        open={openRemoveConfirm}
        onClose={handleCloseRemoveDialog}
        onConfirm={confirmRemoveChildReport}
        childReportDisplayName={
          (selectedChildReport as any)?.childReportDisplayName
        }
        parentReportName={getParentReportName()}
      />

      <CustomSuccessAlert
        open={isSaveChildReportSuccess}
        close={() => setIsSaveChildReportSuccess(false)}
        header={t('PbiChildReport.ChildReportSaveSuccess.Title')}
        message={t('PbiChildReport.ChildReportSaveSuccess.Message')}
        testId="PbiChildReport_Save_Success"
      />

      <CustomSuccessAlert
        open={isEditSaveChildReportSuccess}
        close={() => setIsEditSaveChildReportSuccess(false)}
        header={t('PbiChildReport.ChildReportSaveSuccess.Title')}
        message={t('PbiChildReport.ChildReportSaveSuccess.EditMessage')}
        testId="PbiChildReport_Edit_Success"
      />

      <CustomSuccessAlert
        open={isRemoveSaveChildReportSuccess}
        close={() => setIsRemoveSaveChildReportSuccess(false)}
        header={t('PbiChildReport.ChildReportSaveSuccess.RemoveTitle')}
        message={t('PbiChildReport.ChildReportSaveSuccess.RemoveMessage')}
        testId="PbiChildReport_Remove_Success"
      />
    </Box>
  )
}

export default AdminSettingsPbiChildReport
